import { BedDouble, ChevronRight, Package, Scissors, Sun } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDate } from '../lib/format';
import { StatusPill } from './StatusPill';

export interface BookingSummary {
  id: string;
  type: 'boarding' | 'daycare' | 'grooming' | 'service';
  status: string;
  totalMinor: number;
  createdAt: string;
  updatedAt: string;
  startAt?: string | null;
  endAt?: string | null;
  petName?: string | null;
}

const TYPE_ICON: Record<string, React.ReactElement> = {
  boarding: <BedDouble size={16} />,
  daycare: <Sun size={16} />,
  grooming: <Scissors size={16} />,
  service: <Package size={16} />,
};

const TYPE_LABEL: Record<string, string> = {
  boarding: 'Boarding',
  daycare: 'Daycare',
  grooming: 'Grooming',
  service: 'Service',
};

import type React from 'react';

interface BookingRowProps {
  booking: BookingSummary;
}

export function BookingRow({ booking }: BookingRowProps) {
  const icon = TYPE_ICON[booking.type] ?? <Package size={16} />;
  const typeLabel = TYPE_LABEL[booking.type] ?? booking.type;

  return (
    <Link
      to={`/account/bookings/${booking.id}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.9rem 0',
        textDecoration: 'none',
        color: 'inherit',
        width: '100%',
      }}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '2rem',
          height: '2rem',
          borderRadius: '8px',
          background: 'oklch(0.43 0.18 263 / 0.08)',
          color: 'var(--primary, oklch(0.43 0.18 263))',
          flexShrink: 0,
        }}
      >
        {icon}
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>{typeLabel}</span>
          <StatusPill status={booking.status} />
        </div>
        <div
          style={{
            fontSize: '0.78rem',
            color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
            marginTop: '0.15rem',
          }}
        >
          {booking.petName && <span>{booking.petName} · </span>}
          {booking.startAt ? (
            <span>From {formatDate(booking.startAt)}</span>
          ) : (
            <span>Booked {formatDate(booking.createdAt)}</span>
          )}
        </div>
      </div>

      <ChevronRight
        size={16}
        style={{
          color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          flexShrink: 0,
        }}
      />
    </Link>
  );
}
